import React from 'react'
import cn from 'classnames'
import { FeatureHighlightProvider } from '../../../contexts/feature-highlight-context'
import { CtaButtonMetadataProvider, DefaultCtaButtonContext } from '../../../contexts/cta-button-context'
import { ImageContext, twoColSectionResponsiveImageProps } from '../../../contexts/image-context'

import { useBrowserService } from '../../../lib/hooks'

import {
  TypeSectionTwoColumn,
  CtaDescriptionPosition,
  CtaDescriptionWidth,
  CollapseToSingleColumnBreakpoints,
} from '../../../lib/types'
import { ComponentContentTypes } from '../../../lib/types/content-types'
import { SectionContent } from '../../../lib/types/section'
import { TwoColumnLayout } from '../../layout/two-column'
import { PageSection } from '../../page-section'
import { ContentProps, ContentRenderer, EntryContentProps } from '../../renderer'
import { SpaceMultiple } from '../../ui-lib/spacer'
import styles from './two-column.module.scss'
import { TabSection } from '../tabs/tab-section'
import { TypeHorizontalAlignment } from '../../../lib/types/alignment'

const getAlignmentStyle = (alignment?: TypeHorizontalAlignment) => {
  if (alignment === 'right') {
    return styles.alignRight
  }
  if (alignment === 'left') {
    return styles.alignLeft
  }
  return ''
}

const hasImageOrVideoAssetChild: (c: SectionContent) => boolean = (columnContent: SectionContent) =>
  columnContent.some((col) =>
    [ComponentContentTypes.AssetImage, ComponentContentTypes.AssetVideo].includes(
      col.sys.contentType.sys.id as ComponentContentTypes
    )
  )

const multiplyPadding = (spacingMultiplier?: number) =>
  typeof spacingMultiplier === 'number' ? `${spacingMultiplier * SpaceMultiple}px` : 'auto'

const TwoColumnSectionContent: React.FC<EntryContentProps<TypeSectionTwoColumn>> = (props) => {
  const {
    structure: {
      fields: {
        leftColumnAlignment,
        leftColumnContent,
        rightColumnAlignment,
        rightColumnContent,
        columnsProportion,
        reverseOrderWhenVertical,
        gutterGap,
        collapseToSingleColumnBreakpoint,
      },
    },
  } = props
  const { isMobile } = useBrowserService()
  const additionalSpacing = !isMobile ? 10 : undefined

  const leftAlignmentStyle = getAlignmentStyle(leftColumnAlignment?.fields.horizontal)
  const rightAlignmentStyle = getAlignmentStyle(rightColumnAlignment?.fields.horizontal)

  const getExtraSpacingForColumns = () => {
    // This should only be applied to leftSideColumn
    if (leftColumnContent && hasImageOrVideoAssetChild(leftColumnContent)) {
      return { '--paddingLeft': multiplyPadding(additionalSpacing) } as React.CSSProperties
    }
    // This should only be applied to rightColumnContent
    else if (rightColumnContent && hasImageOrVideoAssetChild(rightColumnContent)) {
      return { '--paddingRight': multiplyPadding(additionalSpacing) } as React.CSSProperties
    }
    return {}
  }

  return (
    <ImageContext.Provider value={twoColSectionResponsiveImageProps}>
      <TwoColumnLayout
        {...{ reverseOrderWhenVertical, columnsProportion, gutterGap, collapseToSingleColumnBreakpoint }}
        leftColumnVerticalAlignment={leftColumnAlignment?.fields.vertical}
        leftContent={
          <div className={cn(styles.contentWithRightSpacing, leftAlignmentStyle)} style={getExtraSpacingForColumns()}>
            <ContentRenderer structure={leftColumnContent} />
          </div>
        }
        rightColumnVerticalAlignment={rightColumnAlignment?.fields.vertical}
        rightContent={
          <div className={cn(styles.contentWithLeftSpacing, rightAlignmentStyle)} style={getExtraSpacingForColumns()}>
            <ContentRenderer structure={rightColumnContent} />
          </div>
        }
      />
    </ImageContext.Provider>
  )
}

export const TwoColumnSection: React.FC<ContentProps<TypeSectionTwoColumn>> = ({ className, structure }) => {
  const {
    sys,
    fields: {
      internalName,
      background,
      heading,
      collapseToSingleColumnBreakpoint = CollapseToSingleColumnBreakpoints.small,
    },
  } = structure

  const descriptionWidth =
    collapseToSingleColumnBreakpoint === CollapseToSingleColumnBreakpoints.small
      ? CtaDescriptionWidth.Fixed
      : CtaDescriptionWidth.Auto

  return (
    <PageSection
      internalName={internalName}
      sysStructure={sys}
      className={className}
      heading={heading}
      background={background}
    >
      <FeatureHighlightProvider value={{ titleStyle: heading ? 'T1' : 'T2' }}>
        <CtaButtonMetadataProvider
          value={{
            ...DefaultCtaButtonContext,
            descriptionWidth,
            descriptionPosition: CtaDescriptionPosition.Left,
          }}
        >
          <TwoColumnSectionContent structure={structure} />
        </CtaButtonMetadataProvider>
      </FeatureHighlightProvider>
    </PageSection>
  )
}

export const TwoColumnSectionForTabs: React.FC<ContentProps<TypeSectionTwoColumn>> = ({ className, structure }) => {
  const {
    fields: { background, heading },
  } = structure

  return (
    <TabSection className={className} heading={heading} background={background}>
      <FeatureHighlightProvider value={{ titleStyle: heading ? 'T1' : 'T2' }}>
        <CtaButtonMetadataProvider
          value={{ ...DefaultCtaButtonContext, descriptionPosition: CtaDescriptionPosition.Left }}
        >
          <TwoColumnSectionContent structure={structure} />
        </CtaButtonMetadataProvider>
      </FeatureHighlightProvider>
    </TabSection>
  )
}
